import { useState, useEffect, useMemo } from 'react';
import { ref, get, update } from 'firebase/database';
import { HiOutlinePlus, HiOutlineViewGrid, HiOutlineTrash } from 'react-icons/hi';
import { db } from '../firebase';

const WATCHLIST_REF = 'TSS-Watchlist';
const ANALYSIS_REF = 'TSS-Analysis';

function formatLastAnalyzed(timestamp) {
  if (timestamp == null || timestamp === '') return '—';
  let ms = null;
  if (typeof timestamp === 'number') {
    ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  } else if (typeof timestamp === 'string') {
    const s = timestamp.trim().replace(/\+00:00Z$/i, 'Z');
    const parsed = Date.parse(s);
    ms = Number.isNaN(parsed) ? Number(timestamp) : parsed;
    if (typeof ms === 'number' && !Number.isNaN(ms) && ms < 1e12 && ms > 0) ms = ms * 1000;
  } else if (typeof timestamp === 'object' && timestamp !== null) {
    const sec = timestamp.seconds ?? timestamp._seconds ?? timestamp.sec;
    if (typeof sec === 'number') ms = sec * 1000;
  }
  if (ms == null || Number.isNaN(ms) || ms <= 0) return '—';
  const now = Date.now();
  const diffMs = now - ms;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  const diffWeek = Math.floor(diffDay / 7);
  if (diffSec < 60) return diffSec <= 1 ? 'just now' : `${diffSec} seconds ago`;
  if (diffMin < 60) return diffMin === 1 ? '1 minute ago' : `${diffMin} minutes ago`;
  if (diffHr < 24) return diffHr === 1 ? '1 hour ago' : `${diffHr} hours ago`;
  if (diffDay < 2) return 'yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  if (diffWeek < 4) return diffWeek === 1 ? '1 week ago' : `${diffWeek} weeks ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const TIMESTAMP_KEYS = ['timestamp', 'lastAnalyzed', 'last_analyzed', 'analyzedAt', 'time', 'lastUpdated'];

function getTimestamp(info) {
  if (!info || typeof info !== 'object') return undefined;
  for (const key of TIMESTAMP_KEYS) {
    const v = info[key];
    if (v != null && v !== '') return v;
  }
  return undefined;
}

function WatchlistContent({ watchlistByGroup, isAdmin, onWatchlistUpdate, showToast }) {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingTicker, setTogglingTicker] = useState(null);
  const [showAddStock, setShowAddStock] = useState(false);
  const [addStockTicker, setAddStockTicker] = useState('');
  const [addStockName, setAddStockName] = useState('');
  const [addStockSector, setAddStockSector] = useState('');
  const [addStockIndustry, setAddStockIndustry] = useState('');
  const [addingStock, setAddingStock] = useState(false);
  const [disableBelowScore, setDisableBelowScore] = useState(1);
  const [applyingDisableBelowScore, setApplyingDisableBelowScore] = useState(false);

  const watchlistCodes = useMemo(() => {
    if (!watchlistByGroup) return [];
    return Object.keys(watchlistByGroup).sort((a, b) => a.localeCompare(b));
  }, [watchlistByGroup]);

  useEffect(() => {
    if (watchlistCodes.length > 0 && (selectedGroup === null || !watchlistCodes.includes(selectedGroup))) {
      setSelectedGroup(watchlistCodes[0]);
    }
  }, [watchlistCodes, selectedGroup]);

  const allStocks = selectedGroup && watchlistByGroup?.[selectedGroup]
    ? Object.entries(watchlistByGroup[selectedGroup]).map(([ticker, info]) => ({
        ticker,
        name: info?.name ?? '—',
        industry: info?.industry ?? '—',
        sector: info?.sector ?? '—',
        timestamp: getTimestamp(info),
        disabled: info?.disabled === true,
      }))
    : [];

  const query = searchQuery.trim().toLowerCase();
  const stocks = query
    ? allStocks.filter((s) =>
        s.ticker.toLowerCase().includes(query) ||
        s.name.toLowerCase().includes(query) ||
        (s.sector !== '—' && s.sector.toLowerCase().includes(query)) ||
        (s.industry !== '—' && s.industry.toLowerCase().includes(query))
      )
    : allStocks;

  const handleToggleDisabled = async (ticker, currentDisabled) => {
    if (!selectedGroup) return;
    setTogglingTicker(ticker);
    const nextDisabled = !currentDisabled;
    try {
      await update(ref(db, `${WATCHLIST_REF}/${selectedGroup}/${ticker}`), { disabled: nextDisabled });
      const next = { ...watchlistByGroup };
      if (next[selectedGroup]) {
        next[selectedGroup] = { ...next[selectedGroup], [ticker]: { ...next[selectedGroup][ticker], disabled: nextDisabled } };
        onWatchlistUpdate(next);
      }
      showToast(nextDisabled ? `${ticker} disabled` : `${ticker} enabled`);
    } catch (err) {
      showToast(err.message || 'Failed to update stock');
    } finally {
      setTogglingTicker(null);
    }
  };

  const handleAddStock = async (e) => {
    e?.preventDefault?.();
    const ticker = addStockTicker.trim().toUpperCase();
    if (!ticker || !selectedGroup) return;
    const existing = watchlistByGroup?.[selectedGroup]?.[ticker];
    if (existing && typeof existing === 'object') {
      showToast(`${ticker} already exists in ${selectedGroup}`);
      return;
    }
    setAddingStock(true);
    try {
      const item = {};
      if (addStockName.trim()) item.name = addStockName.trim();
      if (addStockSector.trim()) item.sector = addStockSector.trim();
      if (addStockIndustry.trim()) item.industry = addStockIndustry.trim();
      await update(ref(db, `${WATCHLIST_REF}/${selectedGroup}/${ticker}`), item);
      const next = { ...watchlistByGroup };
      next[selectedGroup] = { ...(next[selectedGroup] || {}), [ticker]: item };
      onWatchlistUpdate(next);
      setShowAddStock(false);
      setAddStockTicker('');
      setAddStockName('');
      setAddStockSector('');
      setAddStockIndustry('');
      showToast(`${ticker} added to ${selectedGroup}`);
    } catch (err) {
      showToast(err.message || 'Failed to add stock');
    } finally {
      setAddingStock(false);
    }
  };

  const openAddStockModal = () => {
    setAddStockTicker('');
    setAddStockName('');
    setAddStockSector('');
    setAddStockIndustry('');
    setShowAddStock(true);
  };

  const handleDeleteStock = async (ticker) => {
    if (!selectedGroup) return;
    if (!window.confirm(`Delete ${ticker} from ${selectedGroup}?`)) return;
    try {
      await update(ref(db, `${WATCHLIST_REF}/${selectedGroup}`), { [ticker]: null });
      const next = { ...watchlistByGroup };
      if (next[selectedGroup]) {
        next[selectedGroup] = { ...next[selectedGroup] };
        delete next[selectedGroup][ticker];
        onWatchlistUpdate(next);
      }
      showToast(`${ticker} removed from ${selectedGroup}`);
    } catch (err) {
      showToast(err.message || 'Failed to delete stock');
    }
  };

  const parseScore = (row) => {
    if (!row || typeof row !== 'object') return null;
    const v = row.Score ?? row.score;
    const n = typeof v === 'number' ? v : Number(String(v ?? '').trim());
    return Number.isFinite(n) ? n : null;
  };

  const handleDisableBelowScoreChange = async (nextThreshold) => {
    if (!selectedGroup) return;
    setDisableBelowScore(nextThreshold);
    setApplyingDisableBelowScore(true);

    try {
      const snapshot = await get(ref(db, `${ANALYSIS_REF}/${selectedGroup}`));
      const analysis = snapshot.val();
      if (!analysis || typeof analysis !== 'object') {
        showToast('No analysis data to apply');
        return;
      }

      const updates = {};
      const localDisabledByTicker = {};
      const analysisDeletes = {};

      for (const [tickerKey, row] of Object.entries(analysis)) {
        const ticker = (row && typeof row === 'object' ? (row.Ticker ?? row.ticker) : null) ?? tickerKey;
        if (!ticker) continue;
        const score = parseScore(row);
        if (score == null) continue;
        const disabled = score < nextThreshold;
        updates[`${ticker}/disabled`] = disabled;
        localDisabledByTicker[ticker] = disabled;
        if (disabled) analysisDeletes[tickerKey] = null;
      }

      const tickers = Object.keys(localDisabledByTicker);
      if (tickers.length === 0) {
        showToast('No scored items found to apply');
        return;
      }

      await Promise.all([
        update(ref(db, `${WATCHLIST_REF}/${selectedGroup}`), updates),
        Object.keys(analysisDeletes).length > 0 ? update(ref(db, `${ANALYSIS_REF}/${selectedGroup}`), analysisDeletes) : Promise.resolve(),
      ]);

      const next = { ...watchlistByGroup };
      if (next[selectedGroup]) {
        next[selectedGroup] = { ...next[selectedGroup] };
        for (const [ticker, disabled] of Object.entries(localDisabledByTicker)) {
          if (!next[selectedGroup][ticker]) continue;
          next[selectedGroup][ticker] = { ...next[selectedGroup][ticker], disabled };
        }
        onWatchlistUpdate(next);
      }

      showToast(`Applied disable below ${nextThreshold}`);
    } catch (err) {
      showToast(err.message || 'Failed to apply score threshold');
    } finally {
      setApplyingDisableBelowScore(false);
    }
  };

  if (!watchlistByGroup) return null;

  return (
    <>
      <section className="admin-card admin-watchlist-section">
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            <HiOutlineViewGrid className="admin-card-icon" aria-hidden />
            Stocks
          </h2>
        </div>
        <div className="admin-card-body">
          {watchlistCodes.length === 0 ? (
            <p className="admin-placeholder">No watchlists yet. Create one above and add stocks.</p>
          ) : (
            <div className="allstocks-content">
              <nav className="allstocks-tabs" role="tablist" aria-label="Watchlist">
                {watchlistCodes.map((code) => (
                  <button
                    key={code}
                    type="button"
                    role="tab"
                    aria-selected={selectedGroup === code}
                    aria-controls={`admin-watchlist-panel-${code}`}
                    id={`admin-watchlist-tab-${code}`}
                    className={`allstocks-tab ${selectedGroup === code ? 'allstocks-tab--active' : ''}`}
                    onClick={() => setSelectedGroup(code)}
                  >
                    {code}
                  </button>
                ))}
              </nav>
              {watchlistCodes.map((code) => (
                <div
                  key={code}
                  id={`admin-watchlist-panel-${code}`}
                  role="tabpanel"
                  aria-labelledby={`admin-watchlist-tab-${code}`}
                  hidden={selectedGroup !== code}
                  className="allstocks-panel"
                >
                  {selectedGroup === code && (
                    <>
                      <div className="allstocks-toolbar">
                        <div className="allstocks-search-wrap">
                          <input
                            type="search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by ticker, name, sector, industry…"
                            className="allstocks-search"
                            aria-label="Filter stocks"
                          />
                        </div>
                        {isAdmin && (
                          <div className="settings-num-row watchlist-disable-below-score">
                            <span className="settings-input-suffix">Disable if score is below</span>
                            <select
                              value={disableBelowScore}
                              onChange={(e) => handleDisableBelowScoreChange(Number(e.target.value))}
                              disabled={applyingDisableBelowScore}
                              className="settings-select settings-select--inline"
                              aria-label="Disable if score is below"
                            >
                              {Array.from({ length: 100 }, (_, i) => i + 1).map((n) => (
                                <option key={n} value={n}>
                                  {n}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {isAdmin && (
                          <button type="button" className="btn btn-outline watchlist-add-stock-btn" onClick={openAddStockModal}>
                            <HiOutlinePlus className="watchlist-add-icon" aria-hidden />
                            Add stock
                          </button>
                        )}
                      </div>
                      {stocks.length === 0 ? (
                        <div className="allstocks-empty">
                          <p className="admin-placeholder">
                            {allStocks.length === 0 ? 'No stocks in this watchlist.' : 'No stocks match your search.'}
                          </p>
                        </div>
                      ) : (
                        <div className="admin-table-wrap">
                          <table className="admin-table allstocks-table">
                            <thead>
                              <tr>
                                <th>Ticker</th>
                                <th>Name</th>
                                <th>Sector</th>
                                <th>Industry</th>
                                <th>Last analyzed</th>
                                {isAdmin && <th className="allstocks-th-disable">Disable</th>}
                                {isAdmin && <th className="allstocks-th-actions">Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {stocks.map((s) => (
                                <tr key={s.ticker} className={s.disabled ? 'allstocks-row-disabled' : ''}>
                                  <td className="allstocks-cell-ticker">{s.ticker}</td>
                                  <td>{s.name}</td>
                                  <td>{s.sector}</td>
                                  <td>{s.industry}</td>
                                  <td className="allstocks-cell-muted">{formatLastAnalyzed(s.timestamp)}</td>
                                  {isAdmin && (
                                    <>
                                      <td className="allstocks-cell-disable">
                                        <label className="allstocks-toggle">
                                          <input
                                            type="checkbox"
                                            checked={s.disabled}
                                            onChange={() => handleToggleDisabled(s.ticker, s.disabled)}
                                            disabled={togglingTicker === s.ticker}
                                            className="allstocks-toggle-input"
                                            aria-label={`Disable ${s.ticker}`}
                                          />
                                          <span className="allstocks-toggle-slider" />
                                        </label>
                                      </td>
                                      <td className="allstocks-cell-actions">
                                        <button
                                          type="button"
                                          className="btn btn-outline btn-xs"
                                          onClick={() => handleDeleteStock(s.ticker)}
                                          title={`Delete ${s.ticker}`}
                                        >
                                          <HiOutlineTrash aria-hidden style={{ width: '0.8rem', height: '0.8rem' }} />
                                          Delete
                                        </button>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {showAddStock && selectedGroup && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="add-stock-title" aria-describedby="add-stock-desc">
          <div className="modal-dialog">
            <h2 id="add-stock-title" className="modal-title">Add stock to {selectedGroup}</h2>
            <p id="add-stock-desc" className="modal-desc">Add a ticker to the watchlist. Name, sector, and industry are optional.</p>
            <form onSubmit={handleAddStock} className="watchlist-add-form">
              <div className="watchlist-add-field">
                <label htmlFor="add-stock-ticker">Ticker *</label>
                <input id="add-stock-ticker" type="text" value={addStockTicker} onChange={(e) => setAddStockTicker(e.target.value)} placeholder="e.g. AAPL" required disabled={addingStock} autoFocus className="settings-input" />
              </div>
              <div className="watchlist-add-field">
                <label htmlFor="add-stock-name">Name</label>
                <input id="add-stock-name" type="text" value={addStockName} onChange={(e) => setAddStockName(e.target.value)} placeholder="Optional" disabled={addingStock} className="settings-input" />
              </div>
              <div className="watchlist-add-field">
                <label htmlFor="add-stock-sector">Sector</label>
                <input id="add-stock-sector" type="text" value={addStockSector} onChange={(e) => setAddStockSector(e.target.value)} placeholder="Optional" disabled={addingStock} className="settings-input" />
              </div>
              <div className="watchlist-add-field">
                <label htmlFor="add-stock-industry">Industry</label>
                <input id="add-stock-industry" type="text" value={addStockIndustry} onChange={(e) => setAddStockIndustry(e.target.value)} placeholder="Optional" disabled={addingStock} className="settings-input" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowAddStock(false)} disabled={addingStock}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={addingStock || !addStockTicker.trim()}>{addingStock ? 'Adding…' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default WatchlistContent;
